import { forwardRef, useEffect, useState } from "react";
import Modal, { CommonModalProps } from "..";
import {
  Stack,
  Button,
  Grid,
  FormControl,
  InputLabel,
  OutlinedInput,
  FormControlLabel,
  Switch,
  MenuItem,
  Select,
} from "@mui/material";
import FormSection from "../../Forms/FormSection/form-section";
import { cacheService, setupService } from "../../../Core/Api/services";
import useCustomSnackbar from "../../../Utils/Hooks/useCustomSnackbar";
import { DbFormatInfos } from "../../../Core/Api/Types/db-format-infos";
import { FileUsage } from "../../../Core/Api/Types/file-usage";
import { DbStorageInfos } from "../../../Core/Api/Types/db-storage-infos";
import FormAutocomplete from "../../Forms/FormAutocomplete/form-autocomplete";
import { ObjectType } from "../../../Core/Api/Types/object-type";
import { ObjectLayer } from "../../../Core/Api/Types/object-layer";
import { TranskodeWrapper } from "../../../Core/Api/Types/transkode-wrapper";
import { FileLinkedType } from "../../../Core/Api/Types/file-linked-type";
import { ObjectClass } from "../../../Core/Api/Types/object-class";
import { useTranslation } from "react-i18next";

export type ModalFormatEditElementProps = {
  format: DbFormatInfos;
  isCreation: boolean;
  feedbackData: (format: DbFormatInfos) => void;
};

export type ModalFormatEditProps = CommonModalProps<ModalFormatEditElementProps>;

const ModalFormatEdit = forwardRef<any, ModalFormatEditProps>((props, ref) => {
  const { t } = useTranslation();
  const { isCreation, closeModal, feedbackData } = props;
  const [format, setFormat] = useState<DbFormatInfos>(props.format);
  const [storages, setStorages] = useState<DbStorageInfos[]>([]);
  const enqueueSnackbar = useCustomSnackbar();
  const appData = cacheService.GetAppData();

  const handleCancelClick = () => {
    closeModal();
  };

  const handleConfirmClick = () => {
    (isCreation ? setupService.CreateFormat(format) : setupService.UpdateFormat(format)).then((data) => {
      if (!data.success || !data.objectId) {
        enqueueSnackbar(data.message!, "error");
        return;
      }
      feedbackData({ ...format, id: data.objectId });
      enqueueSnackbar(format.name!, "success");
      closeModal();
    });
  };

  useEffect(() => {
    setupService.GetAllStorages().then((data) => {
      setStorages(data.storages ?? []);
    });
    setFormat(
      isCreation
        ? {
            ...props.format,
            platformId: appData?.platformId ?? 0,
            specifications: {
              wrappers: [],
            },
          }
        : props.format
    );
  }, []);

  return (
    <Modal
      title={isCreation ? t("modal:title.formatCreation") : t("modal:title.formatEdit")}
      reference={ref}
      closeModal={handleCancelClick}
      width={800}
      footer={
        <Stack direction="row" justifyContent="space-between">
          <Button onClick={handleCancelClick}>{t("common:cancel")}</Button>
          <Button variant="contained" color="primary" onClick={handleConfirmClick} disabled={false}>
            {t("common:save")}
          </Button>
        </Stack>
      }
    >
      <FormSection title={t("common:generalInformationsLowerCase")} status="todo" defaultExpanded>
        <Grid container spacing={3} flexDirection={"row"}>
          <Grid item md={6}>
            <FormControl sx={{ width: "100%" }}>
              <InputLabel shrink htmlFor="name">
                {t("common:name")}
              </InputLabel>
              <OutlinedInput
                value={format.name ?? ""}
                id="name"
                onChange={(e) => {
                  setFormat({ ...format, name: e.target.value });
                }}
              />
            </FormControl>
          </Grid>
          <Grid item md={6}>
            <InputLabel sx={{ mb: 1 }}>{t("common:usage")}</InputLabel>
            <Select
              value={format?.usage}
              sx={{ width: "100%" }}
              MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
              fullWidth
              onChange={(e) => {
                setFormat({
                  ...format,
                  usage: e.target.value ? FileUsage[e.target.value] : FileUsage.FormatMatcher,
                });
              }}
            >
              {Object.keys(FileUsage).map((type) => (
                <MenuItem value={type} key={type}>
                  {type}
                </MenuItem>
              ))}
            </Select>
          </Grid>
          <Grid item md={6}>
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  onChange={(e, checked) => {
                    setFormat({
                      ...format,
                      isDownloadable: checked,
                    });
                  }}
                  checked={format.isDownloadable}
                />
              }
              labelPlacement="start"
              sx={{ ml: 0, justifyContent: "flex-end", width: "100%", ".MuiFormControlLabel-label": { flex: 1 } }}
              label={t("field:label.isDownloadable")}
            />
          </Grid>
          <Grid item md={6}>
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  onChange={(e, checked) => {
                    setFormat({
                      ...format,
                      isUploadable: checked,
                    });
                  }}
                  checked={format.isUploadable}
                />
              }
              labelPlacement="start"
              sx={{ ml: 0, justifyContent: "flex-end", width: "100%", ".MuiFormControlLabel-label": { flex: 1 } }}
              label={t("field:label.isUploadable")}
            />
          </Grid>
          <Grid item md={6}>
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  onChange={(e, checked) => {
                    setFormat({
                      ...format,
                      isDeletable: checked,
                    });
                  }}
                  checked={format.isDeletable}
                />
              }
              labelPlacement="start"
              sx={{ ml: 0, justifyContent: "flex-end", width: "100%", ".MuiFormControlLabel-label": { flex: 1 } }}
              label={t("field:label.isDeletable")}
            />
          </Grid>
          <Grid item md={6}>
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  onChange={(e, checked) => {
                    setFormat({
                      ...format,
                      isRestricted: checked,
                    });
                  }}
                  checked={format.isRestricted}
                />
              }
              labelPlacement="start"
              sx={{ ml: 0, justifyContent: "flex-end", width: "100%", ".MuiFormControlLabel-label": { flex: 1 } }}
              label={t("field:label.isRestricted")}
            />
          </Grid>
          <Grid item md={6}>
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  onChange={(e, checked) => {
                    setFormat({
                      ...format,
                      enableParsing: checked,
                    });
                  }}
                  checked={format.enableParsing}
                />
              }
              labelPlacement="start"
              sx={{ ml: 0, justifyContent: "flex-end", width: "100%", ".MuiFormControlLabel-label": { flex: 1 } }}
              label={t("field:label.enableFullParsing")}
            />
          </Grid>
          <Grid item md={6}>
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  onChange={(e, checked) => {
                    setFormat({
                      ...format,
                      isUserDataMandatory: checked,
                    });
                  }}
                  checked={format.isUserDataMandatory}
                />
              }
              labelPlacement="start"
              sx={{ ml: 0, justifyContent: "flex-end", width: "100%", ".MuiFormControlLabel-label": { flex: 1 } }}
              label={t("field:label.isUserDataMandatory")}
            />
          </Grid>
          <Grid item md={6}>
            <InputLabel htmlFor="maxfs" sx={{ mb: 1 }}>
              {t("field:label.maxFileSize")}
            </InputLabel>
            <OutlinedInput
              id="maxfs"
              type={"number"}
              value={format.maxFileSizeMB}
              fullWidth
              onChange={(e) => {
                setFormat({
                  ...format,
                  maxFileSizeMB: e.target.value
                    ? parseInt(e.target.value) > 0
                      ? parseInt(e.target.value)
                      : null
                    : null,
                });
              }}
            />
          </Grid>
          {format.usage === FileUsage.UserUpload && storages && (
            <Grid item md={6}>
              <FormAutocomplete
                label={t("field:label.defaultStorage")}
                options={storages}
                placeholder={t("field:placeholder.selectStorage")}
                value={
                  format?.defaultStorageId && storages
                    ? storages.find((x) => x.id === format.defaultStorageId) ?? null
                    : null
                }
                onChange={(e) => {
                  setFormat({
                    ...format,
                    defaultStorageId: e ? parseInt(e.id) : null,
                  });
                }}
                getOptionLabel={(value: any) => value.name}
                isOptionEqualToValue={(option, value) => option?.id === value?.id}
                fullWidth
                validationProps={{
                  formId: "formatvalidation",
                  formSectionId: "general",
                  fieldId: "storage",
                  required: false,
                  initialState: false,
                }}
              />
            </Grid>
          )}
        </Grid>
      </FormSection>
      <FormSection title={t("form:section.title.objectDefinitions")} status="todo" defaultExpanded sx={{ mt: 2 }}>
        <Grid container spacing={3} flexDirection={"row"}>
          <Grid item md={6}>
            <InputLabel sx={{ mb: 1 }}>{t("field:label.fileObjectType")}</InputLabel>
            <Select
              value={format?.objectType}
              sx={{ width: "100%" }}
              MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
              fullWidth
              onChange={(e) => {
                setFormat({
                  ...format,
                  objectType: e.target.value && e.target.value !== "none" ? ObjectType[e.target.value] : null,
                });
              }}
            >
              <MenuItem value={"none"} key={"none"}>
                {t("menu:item.noneHyphen")}
              </MenuItem>
              {Object.keys(ObjectType).map((type) => (
                <MenuItem value={type} key={type}>
                  {type}
                </MenuItem>
              ))}
            </Select>
          </Grid>
          <Grid item md={6}>
            <FormAutocomplete
              label={t("field:label.objectTypesToMatch")}
              options={Object.keys(ObjectType)}
              placeholder={t("field:placeholder.selectObjectType")}
              value={format?.objectTypes ?? []}
              multiple
              onChange={(e) => {
                setFormat({
                  ...format,
                  objectTypes: e,
                });
              }}
              getOptionLabel={(value: any) => value}
              isOptionEqualToValue={(option, value) => option === value}
              fullWidth
              validationProps={{
                formId: "formatvalidation",
                formSectionId: "defs",
                fieldId: "objtypes",
                required: false,
                initialState: false,
              }}
            />
          </Grid>
          <Grid item md={6}>
            <FormAutocomplete
              label={t("field:label.objectLayersToMatch")}
              options={Object.keys(ObjectLayer)}
              placeholder={t("field:placeholder.selectObjectLayer")}
              value={format?.objectLayers ?? []}
              multiple
              onChange={(e) => {
                setFormat({
                  ...format,
                  objectLayers: e,
                });
              }}
              getOptionLabel={(value: any) => value}
              isOptionEqualToValue={(option, value) => option === value}
              fullWidth
              validationProps={{
                formId: "formatvalidation",
                formSectionId: "defs",
                fieldId: "objlayers",
                required: false,
                initialState: false,
              }}
            />
          </Grid>
          <Grid item md={6}>
            <FormAutocomplete
              label={t("field:label.objectClassesToMatch")}
              options={Object.keys(ObjectClass)}
              placeholder={t("field:placeholder.selectObjectClasses")}
              value={format?.objectClasses ?? []}
              multiple
              onChange={(e) => {
                setFormat({
                  ...format,
                  objectClasses: e,
                });
              }}
              getOptionLabel={(value: any) => value}
              isOptionEqualToValue={(option, value) => option === value}
              fullWidth
              validationProps={{
                formId: "formatvalidation",
                formSectionId: "defs",
                fieldId: "objclasses",
                required: false,
                initialState: false,
              }}
            />
          </Grid>
        </Grid>
      </FormSection>
      <FormSection title={t("form:section.title.fileDefinitions")} status="todo" defaultExpanded sx={{ mt: 2 }}>
        <Grid container spacing={3} flexDirection={"row"}>
          <Grid item md={6}>
            <InputLabel sx={{ mb: 1 }}>{t("field:label.fileWrappers")}</InputLabel>
            <Select
              value={format?.specifications?.wrappers ?? []}
              sx={{ width: "100%" }}
              MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
              fullWidth
              multiple
              onChange={(e) => {
                setFormat({
                  ...format,
                  specifications: {
                    wrappers:
                      e.target.value && e.target.value.length > 0 ? (e.target.value as TranskodeWrapper[]) : null,
                  },
                });
              }}
            >
              <MenuItem value={"none"} key={"none"}>
                {t("menu:item.noneHyphen")}
              </MenuItem>
              {Object.keys(TranskodeWrapper).map((type) => (
                <MenuItem value={type} key={type}>
                  {type}
                </MenuItem>
              ))}
            </Select>
          </Grid>
          <Grid item md={6}>
            <FormAutocomplete
              label={t("field:label.fileExtensions")}
              options={[]}
              placeholder={t("field:placeholder.addFileExtensions")}
              value={format?.fileExtensions?.map((x) => x.fileExtension) ?? []}
              multiple
              freeSolo
              onChange={(e) => {
                setFormat({
                  ...format,
                  fileExtensions: (e as string[]).map((x) => ({ fileExtension: x, fileMime: "" })),
                });
              }}
              getOptionLabel={(value: any) => value}
              isOptionEqualToValue={(option, value) => option === value}
              fullWidth
              validationProps={{
                formId: "formatvalidation",
                formSectionId: "defs",
                fieldId: "filexts",
                required: false,
                initialState: false,
              }}
            />
          </Grid>
          <Grid item md={6}>
            <InputLabel sx={{ mb: 1 }}>{t("field:label.fileLinkedType")}</InputLabel>
            <Select
              value={format?.linkedType}
              sx={{ width: "100%" }}
              MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
              fullWidth
              onChange={(e) => {
                setFormat({
                  ...format,
                  linkedType: e.target.value && e.target.value !== "none" ? FileLinkedType[e.target.value] : null,
                });
              }}
            >
              <MenuItem value={"none"} key={"none"}>
                {t("menu:item.noneHyphen")}
              </MenuItem>
              {Object.keys(FileLinkedType).map((type) => (
                <MenuItem value={type} key={type}>
                  {type}
                </MenuItem>
              ))}
            </Select>
          </Grid>
        </Grid>
      </FormSection>
    </Modal>
  );
});

export default ModalFormatEdit;
